import { useMemo } from 'react';

/**
 * How one field of a record is compared against what the dialog opened with.
 * Comparators must be referentially stable (module constants), or every render
 * recomputes the diff.
 */
export interface FieldComparator<V> {
  /** Applied to both sides before comparing, and to the value placed in `changes`. */
  normalize?: (value: V) => V;
  /** Replaces Object.is after normalize. */
  equals?: (a: V, b: V) => boolean;
}

export type RecordComparators<T> = { [K in keyof T]?: FieldComparator<T[K]> };

export interface RecordChanges<T> {
  /** Only the fields that differ, with normalized values. */
  changes: Partial<T>;
  changeCount: number;
  isDirty: boolean;
}

/** Text that differs only by surrounding whitespace is unchanged. */
export const trimmedText: FieldComparator<string> = {
  normalize: (value) => value.trim(),
};

/**
 * JSON text, compared by parsed value (key order counts). Whitespace-only
 * reformatting is unchanged; text that won't parse is unequal to anything but
 * the identical text, so a half-typed object reads as dirty.
 */
export const jsonText: FieldComparator<string> = {
  equals: (a, b) => {
    if (a === b) return true;
    try {
      return JSON.stringify(JSON.parse(a)) === JSON.stringify(JSON.parse(b));
    } catch {
      return false;
    }
  },
};

/** Two sets with the same members, in any order. */
export const sameSet: FieldComparator<Set<string>> = {
  equals: (a, b) => a.size === b.size && [...a].every((member) => b.has(member)),
};

/** Numbers typed as text: '1.50' equals '1.5'; non-numbers compare as trimmed text. */
export const numericText: FieldComparator<string> = {
  equals: (a, b) => {
    const left = a.trim();
    const right = b.trim();
    if (left === right) return true;
    if (left === '' || right === '') return false;
    const l = Number(left);
    const r = Number(right);
    return Number.isFinite(l) && Number.isFinite(r) && l === r;
  },
};

/**
 * What an edit dialog's form holds that the record it opened with does not
 * (DESIGN_SYSTEM.md §5 Record workbench). "Dirty" is "differs from the
 * baseline", never "touched": typing a character and deleting it again is clean.
 *
 * The baseline must be STATE set when the dialog opens and when a save lands —
 * never a memo over the incoming record. A page replaces the record object
 * whenever anything writes, and a memo would rebuild the baseline mid-edit:
 * Save would go quiet and unsaved typing would look saved.
 *
 * `baseline` null means nothing is loaded yet, which is clean.
 */
export function useRecordChanges<T extends object>(
  baseline: T | null,
  form: T,
  comparators?: RecordComparators<T>,
): RecordChanges<T> {
  return useMemo(() => {
    const changes: Partial<T> = {};
    if (baseline) {
      for (const key of Object.keys(form) as Array<keyof T>) {
        const comparator = comparators?.[key];
        const normalize = comparator?.normalize ?? ((value: T[typeof key]) => value);
        const next = normalize(form[key]);
        const previous = normalize(baseline[key]);
        const same = comparator?.equals ? comparator.equals(next, previous) : Object.is(next, previous);
        if (!same) changes[key] = next;
      }
    }
    const changeCount = Object.keys(changes).length;
    return { changes, changeCount, isDirty: changeCount > 0 };
  }, [baseline, form, comparators]);
}
